import { useState, useRef, useEffect } from 'react';

import { useUtils } from '../../../../core/hooks/useUtils';
import {
	MODAL_TYPES,
	useGlobalModalContext,
} from '../../../../shared/global-modal';
import {
	exportChartAction,
	inviteMembersAction,
	shareChartAction,
} from '../../../../store/Charts/action';
import { removeFavoriteAction } from '../../../../store/Favorites/action';
import { exportSnapAction } from '../../../../store/Snaps/action';
import { removeFromSnapshotsAction } from '../../../../store/Snapshots/action';

interface IOutput {
	state: HookState;
	inviteMembers(type: string): void;
	exportChart(type: string): void;
	exportSnap(id: string): void;
	toggleOptions(): void;
	onDeleteSnapshot(id: string): void;
	onDeleteFavorite(id: string): void;
	onShare(type: string): void;
	openDetails(): void;
	useComponentVisible(): any;
}

interface HookState {
	isLoadingRooms: boolean;
	isLoadingExport: boolean;
	isDetailVisible: boolean;
	isDeleting: boolean;
	isVisibleSharing: boolean;
	isDetailsOpen: boolean;
	isCopied: boolean;
	link: string;
	isLoadingSharing: boolean;
}

export default function useHook(): IOutput {
	const { showModal } = useGlobalModalContext();
	const { dispatch } = useUtils();

	const [state, setState] = useState<HookState>({
		isLoadingRooms: false,
		isLoadingExport: false,
		isDetailVisible: false,
		isDeleting: false,
		isVisibleSharing: false,
		isCopied: false,
		link: '',
		isLoadingSharing: false,
		isDetailsOpen: false,
	});

	const inviteMembers = (type: string): void => {
		toggleLoader('isLoadingRooms');
		dispatch(
			inviteMembersAction({
				type,
				showSuccessModal: () => {
					toggleLoader('isLoadingRooms');
					showModal(MODAL_TYPES.SUCCESS_MODAL, {
						title: 'All members have been successfully invited',
						confirmBtn: 'Continue',
					});
				},
				showFailModal,
			})
		);
	};

	const openDetails = (): void => {
		setState({ ...state, isDetailsOpen: !state.isDetailsOpen });
	};

	const useComponentVisible = () => {
		const ref = useRef<HTMLDivElement>(null);
		const handleClickOutside = (event: Event) => {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				setState(prev => ({
					...prev,
					isDetailsOpen: false,
				}));
			}
		};
		useEffect(() => {
			document.addEventListener('click', handleClickOutside, true);
			return () => {
				document.removeEventListener('click', handleClickOutside, true);
			};
		});

		return { ref };
	};

	const exportChart = (type: string): void => {
		toggleLoader('isLoadingExport');
		dispatch(
			exportChartAction({
				type,
				showSuccessModal: () => {
					toggleLoader('isLoadingExport');
					showModal(MODAL_TYPES.SUCCESS_MODAL, {
						title: 'We have sent you a csv file with this report data',
						confirmBtn: 'Continue',
					});
				},
				showFailModal,
			})
		);
	};

	const exportSnap = (id: string): void => {
		toggleLoader('isLoadingExport');
		dispatch(
			exportSnapAction({
				id,
				showSuccessModal: () => {
					toggleLoader('isLoadingExport');
					showModal(MODAL_TYPES.SUCCESS_MODAL, {
						title: 'We have sent you a csv file with this report data',
						confirmBtn: 'Continue',
					});
				},
				showFailModal,
			})
		);
	};

	const showFailModal = () => {
		toggleLoader('isLoadingExport');
		showModal(MODAL_TYPES.FAIL_MODAL, {
			title: 'Something went wrong!',
			confirmBtn: 'Continue',
		});
	};

	const toggleLoader = (field: keyof typeof state): void => {
		setState(prev => ({
			...prev,
			[field]: !prev[field],
		}));
	};

	const toggleOptions = (): void => {
		setState(prev => ({
			...prev,
			isDetailVisible: !prev.isDetailVisible,
		}));
	};

	const confirmDeleteSnapshot = (_id: string): void => {
		toggleLoader('isDeleting');
		dispatch(
			removeFromSnapshotsAction({
				_id,
				callback: () => {
					toggleLoader('isDeleting');
				},
			})
		);
	};

	const onDeleteSnapshot = (_id: string): void => {
		showModal(MODAL_TYPES.CONFIRM_MODAL, {
			title: 'Are you sure you want to delete this report?',
			confirmBtn: 'Delete',
			confirmCallback: () => confirmDeleteSnapshot(_id),
		});
	};

	const confirmDeleteFavorite = (_id: string): void => {
		toggleLoader('isDeleting');
		dispatch(
			removeFavoriteAction({
				_id,
				callback: () => {
					toggleLoader('isDeleting');
				},
			})
		);
	};

	const onDeleteFavorite = (_id: string): void => {
		showModal(MODAL_TYPES.CONFIRM_MODAL, {
			title: 'Are you sure you want to delete this report?',
			confirmBtn: 'Delete',
			confirmCallback: () => confirmDeleteFavorite(_id),
		});
	};

	const onShare = (type: string): void => {
		if (state.isCopied) return;
		toggleLoader('isLoadingSharing');
		dispatch(
			shareChartAction({
				type,
				callback: (link: string) => {
					setState(prev => ({ ...prev, link }));
					navigator.clipboard.writeText(link);
					toggleLoader('isCopied');
					toggleLoader('isLoadingSharing');
				},
			})
		);
	};

	return {
		state,
		useComponentVisible,
		openDetails,
		inviteMembers,
		exportChart,
		exportSnap,
		toggleOptions,
		onDeleteSnapshot,
		onDeleteFavorite,
		onShare,
	};
}
